"use client";

import { BuiltInProviderType } from "next-auth/providers/index";
import {
    ClientSafeProvider,
    getProviders,
    signIn,
    useSession,
} from "next-auth/react";
import { useEffect, useRef, useState } from "react";
import { LiteralUnion } from "react-hook-form";
import { FcGoogle } from "react-icons/fc";
import { Button, ButtonLink } from "../v3/themed/Button";
import { If } from "../v3/Conditional";
import { sanitizeEmail } from "~/utils/formatters";
import { cn } from "~/utils/fontsConfig";
import Script from "next/script";
import { env } from "~/env.mjs";
import { usePathname } from "next/navigation";
import { CustomizableLayout } from "~/types/CustomizableLayout";
import {
    InputOTP,
    InputOTPGroup,
    InputOTPSlot,
} from "~/components/v3/shadcn/InputOTP";
import Spinner from "~/components/themed/spinner";
import { RxCheck } from "react-icons/rx";
import { captureMessage } from "@sentry/nextjs";

interface GenericAuthProps {
    mode: "login" | "signup";
    variant: "modal" | "page";
    callbackUrl?: string;
    hideTitle?: boolean;
    allowGuests?: boolean;
    useInPlaceFlow?: boolean;
    onBack?: () => void;
    setIsWaitingForMagicLink?: (isWaiting: boolean) => void;
    customization?: CustomizableLayout;
}

const GenericAuth = ({
    mode: modeProp,
    variant,
    callbackUrl,
    hideTitle = false,
    allowGuests = false,
    useInPlaceFlow = false,
    onBack,
    setIsWaitingForMagicLink,
    customization,
}: GenericAuthProps) => {
    const { status, update } = useSession();
    const [email, setEmail] = useState<string>("");
    const [mode, setMode] = useState(modeProp);
    const [password, setPassword] = useState<string>("");
    const [isValidEmail, setIsValidEmail] = useState<boolean>(false);
    const [isVerifying, setIsVerifying] = useState<boolean>(false);
    const [otp, setOtp] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const companyRef = useRef<HTMLInputElement>(null);
    const pathname = usePathname();

    // https://ryanschiang.com/nextjs-cloudflare-turnstile
    useEffect(() => {
        const turnstileContainers = document.querySelectorAll(".cf-turnstile");

        turnstileContainers.forEach((turnstileContainer) => {
            turnstileContainer.innerHTML = "";
            if (window && window.turnstile) {
                window.turnstile.render(turnstileContainer, {
                    sitekey: env.NEXT_PUBLIC_CF_TURNSTILE_SITE_KEY,
                    callback: "javascriptCallback",
                });
            }
        });
    }, [pathname]);

    const [providers, setProviders] = useState<Record<
        LiteralUnion<BuiltInProviderType, string>,
        ClientSafeProvider
    > | null>(null);

    useEffect(() => {
        const fetchProviders = async () => {
            const providers = await getProviders();
            setProviders(providers);
        };

        fetchProviders();
        if (companyRef.current) companyRef.current.required = false;
    }, []);

    const validateEmail = (email: string) => {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(email);
    };

    const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const emailValue = event.target.value;
        setEmail(emailValue);
        setIsValidEmail(validateEmail(emailValue));
    };

    const handlePasswordChange = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const passwordValue = event.target.value;
        setPassword(passwordValue);
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const captcha = document
            .querySelector("input[name='cf-turnstile-response']")
            ?.getAttribute("value");

        if (isValidEmail) {
            const form = event.currentTarget;
            const companyInput = form.elements.namedItem(
                "company",
            ) as HTMLInputElement;

            setIsLoading(true);
            const result = await signIn("magic-code-login", {
                email: sanitizeEmail(email),
                redirect: false,
                callbackUrl,
                captcha,
                company: companyInput?.value || "",
            });

            if (result?.ok) {
                setIsVerifying(true);
                setIsWaitingForMagicLink?.(true);
            }
            setIsLoading(false);
        }
    };

    const verifyOTP = async (otp: string, identifier: string) => {
        const resp = await fetch(
            `/api/auth/callback/email?email=${encodeURIComponent(identifier)}&token=${otp}`,
        );
        return resp.status === 200 && !resp.url.includes("error");
    };

    useEffect(() => {
        const checkOtp = async () => {
            if (otp.length === 6) {
                setIsLoading(true);
                const isValid = await verifyOTP(otp, email.toLowerCase());
                if (isValid) {
                    window.location.href = callbackUrl || "/";
                } else {
                    setErrorMessage("Invalid verification code");
                }
                setIsLoading(false);
            }
        };
        checkOtp();
    }, [otp, email, callbackUrl]);

    return (
        <div className={cn("flex w-full flex-col items-center px-6")}>
            <div className={"w-full max-w-md text-white"}>
                <If condition={!hideTitle}>
                    <h6 className={cn("mb-4", customization?.labelClassName)}>
                        {isVerifying
                            ? "Enter verification code"
                            : mode === "login"
                              ? "Log in to River"
                              : "Sign up to start attending events"}
                    </h6>
                </If>

                {!isVerifying ? (
                    <>
                        {providers?.google && (
                            <div className="mb-3">
                                <Button
                                    onClick={() =>
                                        signIn(providers.google.id, {
                                            callbackUrl,
                                        })
                                    }
                                    variant="tertiary"
                                    className={cn(
                                        "w-full rounded-full",
                                        customization?.buttons?.secondary,
                                    )}
                                >
                                    <FcGoogle className="mr-4" />
                                    {mode === "login"
                                        ? "Continue"
                                        : "Sign up with"}{" "}
                                    with Google
                                </Button>
                            </div>
                        )}

                        <div className="my-4" />

                        <div className="my-4 flex items-center justify-center">
                            <div className="flex-grow border-t border-gray-300"></div>
                            <span
                                className={cn(
                                    "flex-shrink px-4 text-sm tracking-wide text-gray-600",
                                    customization?.descriptionClassName,
                                )}
                            >
                                or
                            </span>
                            <div className="flex-grow border-t border-gray-300"></div>
                        </div>

                        <form className="space-y-4" onSubmit={handleSubmit}>
                            <div>
                                <label
                                    htmlFor="email"
                                    className={cn(
                                        "sr-only",
                                        customization?.labelClassName,
                                    )}
                                >
                                    Email address
                                </label>
                                <input
                                    id="email"
                                    name="email"
                                    type="email"
                                    autoComplete="email"
                                    required
                                    value={email}
                                    onChange={handleEmailChange}
                                    className={cn(
                                        "relative block w-full rounded-md border border-gray-300 bg-transparent px-3 py-2 text-sm placeholder-gray-500 shadow-sm focus:outline-none focus:ring-1 focus:ring-offset-2",
                                        customization?.inputClassName,
                                    )}
                                    placeholder="Email address"
                                />
                                <input
                                    id="company"
                                    ref={companyRef}
                                    type="text"
                                    name="company"
                                    placeholder="Company name"
                                    className={cn(
                                        "register-company relative block w-full rounded-md border border-gray-300 bg-transparent px-3 py-2 text-sm placeholder-gray-500 shadow-sm focus:outline-none focus:ring-1 focus:ring-offset-2",
                                        customization?.inputClassName,
                                    )}
                                />
                            </div>
                            <div>
                                <Button
                                    type="submit"
                                    className={cn(
                                        "w-full",
                                        customization?.buttons?.default,
                                    )}
                                    disabled={!isValidEmail || isLoading}
                                >
                                    {isLoading ? (
                                        <Spinner
                                            type="small"
                                            className="h-6 w-6"
                                        />
                                    ) : mode === "login" ? (
                                        "Log in"
                                    ) : (
                                        "Sign up"
                                    )}
                                </Button>
                            </div>
                            <Script
                                src="https://challenges.cloudflare.com/turnstile/v0/api.js"
                                async
                                defer
                            ></Script>
                            <div
                                className="cf-turnstile"
                                data-sitekey={
                                    env.NEXT_PUBLIC_CF_TURNSTILE_SITE_KEY
                                }
                                data-appearance="interaction-only"
                                data-callback="javascriptCallback"
                            ></div>
                        </form>
                    </>
                ) : (
                    <div className="space-y-4">
                        <div className="flex justify-center overflow-hidden">
                            <InputOTP
                                maxLength={6}
                                value={otp}
                                onChange={(value) => setOtp(value)}
                                disabled={isLoading}
                                containerClassName="has-[:disabled]:opacity-20"
                            >
                                <InputOTPGroup>
                                    <InputOTPSlot
                                        className={cn(
                                            "text-md h-12 w-12 md:h-14 md:w-14 md:text-xl",
                                            customization?.defaultTheme ===
                                                "light"
                                                ? "border-black text-black"
                                                : "text-white",
                                        )}
                                        index={0}
                                    />
                                    <InputOTPSlot
                                        className={cn(
                                            "text-md h-12 w-12 md:h-14 md:w-14 md:text-xl",
                                            customization?.defaultTheme ===
                                                "light"
                                                ? "border-black text-black"
                                                : "text-white",
                                        )}
                                        index={1}
                                    />
                                    <InputOTPSlot
                                        className={cn(
                                            "text-md h-12 w-12 md:h-14 md:w-14 md:text-xl",
                                            customization?.defaultTheme ===
                                                "light"
                                                ? "border-black text-black"
                                                : "text-white",
                                        )}
                                        index={2}
                                    />
                                    <InputOTPSlot
                                        className={cn(
                                            "text-md h-12 w-12 md:h-14 md:w-14 md:text-xl",
                                            customization?.defaultTheme ===
                                                "light"
                                                ? "border-black text-black"
                                                : "text-white",
                                        )}
                                        index={3}
                                    />
                                    <InputOTPSlot
                                        className={cn(
                                            "text-md h-12 w-12 md:h-14 md:w-14 md:text-xl",
                                            customization?.defaultTheme ===
                                                "light"
                                                ? "border-black text-black"
                                                : "text-white",
                                        )}
                                        index={4}
                                    />
                                    <InputOTPSlot
                                        className={cn(
                                            "text-md h-12 w-12 md:h-14 md:w-14 md:text-xl",
                                            customization?.defaultTheme ===
                                                "light"
                                                ? "border-black text-black"
                                                : "text-white",
                                        )}
                                        index={5}
                                    />
                                </InputOTPGroup>
                            </InputOTP>
                        </div>

                        <div
                            className={cn(
                                "flex flex-col items-center justify-center gap-2 text-center text-xs",
                                customization?.descriptionClassName,
                            )}
                        >
                            <If condition={!otp && !isLoading}>
                                Enter one-time password from your email inbox.
                            </If>

                            <If condition={!!errorMessage}>
                                <span className="text-red-500">
                                    {errorMessage}
                                </span>
                            </If>
                            <If condition={isLoading}>
                                <Spinner type="small" className="h-6 w-6" />
                            </If>
                        </div>

                        <Button
                            onClick={(e) => {
                                e.preventDefault();
                                setIsVerifying(false);
                                setIsWaitingForMagicLink?.(false);
                                onBack?.();
                            }}
                            variant="tertiary"
                            className={cn(
                                "w-full",
                                customization?.buttons?.secondary,
                            )}
                        >
                            Use a different email
                        </Button>
                    </div>
                )}

                {providers?.credentials &&
                    process.env.NODE_ENV !== "production" && (
                        <>
                            <div className="my-6 flex items-center justify-center">
                                <div className="flex-grow border-t border-gray-300"></div>
                                <span
                                    className={cn(
                                        "flex-shrink px-4 text-sm tracking-wide text-gray-600",
                                        customization?.descriptionClassName,
                                    )}
                                >
                                    or
                                </span>
                                <div className="flex-grow border-t border-gray-300"></div>
                            </div>

                            <form className="space-y-6">
                                <div>
                                    <label
                                        htmlFor="credentials-email"
                                        className="sr-only"
                                    >
                                        Email address
                                    </label>
                                    <input
                                        id="credentials-email"
                                        name="credentials-email"
                                        type="email"
                                        autoComplete="email"
                                        required
                                        value={email}
                                        onChange={handleEmailChange}
                                        className={cn(
                                            "relative block w-full rounded-md border border-gray-300 bg-transparent px-3 py-2 text-sm placeholder-gray-500 shadow-sm focus:outline-none focus:ring-1 focus:ring-offset-2",
                                            customization?.inputClassName,
                                        )}
                                        placeholder="Email address"
                                    />
                                </div>

                                <div>
                                    <label
                                        htmlFor="email"
                                        className={cn(
                                            "sr-only",
                                            customization?.labelClassName,
                                        )}
                                    >
                                        Password
                                    </label>
                                    <input
                                        id="credentials-password"
                                        name="credentials-password"
                                        type="password"
                                        autoComplete="password"
                                        required
                                        value={password}
                                        onChange={handlePasswordChange}
                                        className={cn(
                                            "relative block w-full rounded-md border border-gray-300 bg-transparent px-3 py-2 text-sm placeholder-gray-500 shadow-sm focus:outline-none focus:ring-1 focus:ring-offset-2",
                                            customization?.inputClassName,
                                        )}
                                        placeholder="Password"
                                    />
                                </div>
                                <div>
                                    <Button
                                        onClick={() =>
                                            signIn(providers?.credentials.id, {
                                                email,
                                                password,
                                            })
                                        }
                                        type="button"
                                        variant="tertiary"
                                        className={cn(
                                            "w-full rounded-full",
                                            customization?.buttons?.secondary,
                                        )}
                                    >
                                        Sign in with Credentials
                                    </Button>
                                </div>
                            </form>
                        </>
                    )}

                <If condition={!allowGuests}>
                    <div className="mt-4 text-center text-sm">
                        <If condition={variant === "modal"}>
                            <If condition={mode === "login"}>
                                {" "}
                                Don't have an account yet?
                                <Button
                                    onClick={() => setMode("signup")}
                                    variant="secondaryLink"
                                    className={cn(
                                        "font-medium",
                                        customization?.linkClassName,
                                    )}
                                >
                                    Sign up here
                                </Button>
                            </If>
                            <If condition={mode === "signup"}>
                                {" "}
                                <span
                                    className={cn(
                                        customization?.descriptionClassName,
                                    )}
                                >
                                    Already have an account?
                                </span>
                                <Button
                                    onClick={() => setMode("login")}
                                    variant="secondaryLink"
                                    className={cn(
                                        "font-medium",
                                        customization?.linkClassName,
                                    )}
                                >
                                    Log in here
                                </Button>
                            </If>
                        </If>
                        <If condition={variant === "page"}>
                            <If condition={mode === "login"}>
                                <a
                                    href="/signup"
                                    className={cn(
                                        "font-medium",
                                        customization?.linkClassName,
                                    )}
                                >
                                    {" "}
                                    Don't have an account yet? Sign up here
                                </a>
                            </If>
                            <If condition={mode === "signup"}>
                                <a
                                    href="/login"
                                    className={cn(
                                        "font-medium",
                                        customization?.linkClassName,
                                    )}
                                >
                                    {" "}
                                    Already have an account? Log in here
                                </a>
                            </If>
                        </If>
                    </div>
                </If>
            </div>

            <span
                className={cn(
                    "mt-4 text-center text-[10px] italic",
                    customization?.descriptionClassName,
                )}
            >
                This site is protected by the Google Privacy Policy and Terms of
                Service apply
            </span>
        </div>
    );
};

export default GenericAuth;
